import React, { Component, Fragment } from "react";
import clsx from "clsx";
import PropTypes from "prop-types";
import { Resizable } from "re-resizable";
import screenfull from "screenfull";
import DragHandleIcon from "@mui/icons-material/DragHandle";

import { DLS_MODE } from "~/constants/presentation-modes";
import { zoomControllerType } from "~/constants/prop-types";
import EventBus from "~/libs/eventBus";
import { getThumbnailPath, getImagePath } from "~/libs/image";
import { bindShortcut } from "~/libs/keyboard";
import { getQuickDialActions } from "~/utils/quickdial";
import { getNoteId } from "~/utils";
import { styled } from "@mui/material/styles";

import Pager from "~/components/Pager";
import Pagination from "~/components/Pagination";
import TeacherColumn from "~/components/TeacherColumn";
import Viewer from "~/components/Viewer";
import { connect } from "react-redux";
import { selectPadSidebarLeft, selectPadSidebarRight } from "~/store/ducks";

import AnnotationsMenu from "./AnnotationsMenu";
import { hideModal, selectKind, selectModal } from "~/store/ducks/domain/ui/modal";

const Content = styled("div")(props => ({
  height: "100%",
  display: "flex",
  overflow: "hidden",
  paddingLeft: props.padLeftSide ? 300 : 0,
  paddingRight: props.padRightSide ? 300 : 0,
  "@media print": {
    padding: "0 !important"
  }
}));

const Wrapper = styled("div")(() => ({
  height: "calc(100vh - 48px)",
  flex: "1 1 auto",
  display: "flex",
  flexFlow: "column",
  "@media print": {
    height: "100% !important"
  }
}));

const VerticalDivider = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgb(224, 225, 225)",
  minWidth: theme.typography.pxToRem(14),
  maxWidth: theme.typography.pxToRem(14),
  height: "100%",
  cursor: "col-resize",
  marginLeft: 5
}));

const StyledDragHandleIcon = styled(DragHandleIcon)(() => ({
  transform: "rotate(90deg)"
}));

class Annotationeer extends Component {
  static whyDidYouRender = false;

  constructor(props) {
    super(props);

    this.renderPagination = this.renderPagination.bind(this);
    this.handleResizeStop = this.handleResizeStop.bind(this);
    this.setResizableRef = this.setResizableRef.bind(this);
    this.getResizablePermission = this.getResizablePermission.bind(this);
    this.getResizableStyle = this.getResizableStyle.bind(this);
  }

  componentDidMount() {
    const { zoomController, setFullScreen, goToNext, goToPrevious, displayMode } = this.props;

    const { zoomToFit, zoomIn, zoomOut } = zoomController;

    if (screenfull.isEnabled) {
      screenfull.on("change", () => {
        setFullScreen(!screenfull.isFullscreen);
      });
    }

    const shortcuts = [
      [["mod++", "mod+="], zoomIn, true],
      [["mod+-"], zoomOut, true],
      [["mod+0"], zoomToFit, true],
      [["left"], goToPrevious, true],
      [["right"], goToNext, true]
      // [
      //   ["esc"],
      //   () => {
      //     // hideModal();
      //     console.log("select modal", selectKind());
      //   },
      //   true
      // ]
    ];
    shortcuts.map(bindShortcut);

    if (displayMode === DLS_MODE) {
      EventBus.dispatch("zoomToFit", "main", {
        direction: "top",
        padding: { top: 48 }
      });
    } else {
      EventBus.dispatch("zoomToFit", "main");
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.displayMode !== prevProps.displayMode) {
      if (this.props.displayMode === DLS_MODE) {
        EventBus.dispatch("zoomToFit", "main", {
          direction: "top",
          padding: { top: 48 }
        });
      } else {
        EventBus.dispatch("zoomToFit", "main");
      }
    }

    if (this.props.pageMode !== prevProps.pageMode) {
      EventBus.dispatch("zoomToFit", "main");
    }
  }

  setResizableRef(resizable) {
    this.resizable = resizable;
  }

  handleResizeStop() {
    EventBus.dispatch("zoomToFitAgain", "main");
    EventBus.dispatch("zoomToFitAgain", "embedded");
    this.props.setResizableWidth(this.resizable ? this.resizable.size.width : null);
  }

  getResizablePermission() {
    return {
      top: false,
      right: false,
      bottom: false,
      left: true,
      topRight: false,
      bottomRight: false,
      bottomLeft: false,
      topLeft: false
    };
  }

  getResizableStyle() {
    return {
      display: "flex",
      paddingLeft: "1.25rem"
    };
  }

  getResizableDefaultSize() {
    const { resizableWidth } = this.props;
    return {
      width: resizableWidth || "80%",
      height: "100%"
    };
  }

  renderHandle() {
    return {
      left: (
        <VerticalDivider>
          <StyledDragHandleIcon color="action" />
        </VerticalDivider>
      )
    };
  }

  renderPagination() {
    const {
      pagesCount,
      goToPrevious,
      goToNext,
      isGreaterThanSmall,
      absolutePage,
      isDlsActive,
      relativePage,
      goToDestination
    } = this.props;

    const isLeftPagerVisible = absolutePage !== 1 && pagesCount !== 0 && isGreaterThanSmall;

    const isRightPagerVisible = absolutePage !== pagesCount && pagesCount !== 0 && isGreaterThanSmall;

    return (
      <>
        {isDlsActive && (
          <Pagination
            page={relativePage}
            goToPrevious={goToPrevious}
            goToNext={goToNext}
            goToDestination={goToDestination}
            showGoToPrevious={isLeftPagerVisible}
            showGoToNext={isRightPagerVisible}
          />
        )}

        {!isDlsActive && isLeftPagerVisible && <Pager direction="left" onClick={goToPrevious} />}

        {!isDlsActive && isRightPagerVisible && <Pager direction="right" onClick={goToNext} />}
      </>
    );
  }

  render() {
    const {
      activePages,
      zoomController,
      isDoublePage,
      pageWidth,
      pageHeight,
      isDlsActive,
      relativePage,
      showModal,
      isPresentationMode,
      isAbleToPrint,
      notesDlsOnly,
      bookTitle,
      pagesHaveNotes,
      showCutout,
      padLeftSide,
      padRightSide
    } = this.props;

    const totalPageWidth = activePages.length === 2 ? pageWidth * 2 : pageWidth;
    const totalPageHeight = pageHeight;

    return (
      <Wrapper className="annotationeer-wrapper">
        <AnnotationsMenu />

        <Content className="annotationeer-content" padLeftSide={padLeftSide} padRightSide={padRightSide}>
          <Viewer
            name="main"
            bookId=""
            page={relativePage}
            zoomController={zoomController}
            isDoublePage={isDoublePage}
            pages={activePages}
            renderPagination={this.renderPagination}
            pageWidth={pageWidth}
            pageHeight={pageHeight}
            totalPageWidth={totalPageWidth}
            totalPageHeight={totalPageHeight}
            getThumbnailPath={getThumbnailPath}
            getImagePath={getImagePath}
            showAnnotations={true}
            showMaterials={true}
            showCutout={showCutout}
            quickDialActions={getQuickDialActions({
              zoomController,
              printOrigin: "main",
              showModal,
              isPresentationMode,
              isAbleToPrint,
              currentPage: relativePage,
              noteId: getNoteId(bookTitle, relativePage),
              dlsOnly: notesDlsOnly,
              isDlsMode: isDlsActive,
              pagesHaveNotes
            })}
          />

          {isDlsActive && (
            <Resizable
              className="teacher-column-wrapper"
              handleWrapperClass="teacher-column-handle-wrapper"
              style={this.getResizableStyle()}
              ref={this.setResizableRef}
              defaultSize={this.getResizableDefaultSize()}
              maxWidth="90%"
              minWidth="10%"
              enable={this.getResizablePermission()}
              onResizeStop={this.handleResizeStop}
              handleComponent={this.renderHandle()}
            >
              <TeacherColumn activePages={activePages} />
            </Resizable>
          )}
        </Content>
      </Wrapper>
    );
  }
}

const mapStateToProps = state => {
  return {
    padLeftSide: selectPadSidebarLeft(state),
    padRightSide: selectPadSidebarRight(state)
  };
};

Annotationeer.propTypes = {
  isGreaterThanSmall: PropTypes.bool.isRequired,
  goToNext: PropTypes.func.isRequired,
  goToPrevious: PropTypes.func.isRequired,
  zoomController: zoomControllerType.isRequired,
  setFullScreen: PropTypes.func,
  displayMode: PropTypes.string,
  pageMode: PropTypes.number,
  pagesCount: PropTypes.number,
  absolutePage: PropTypes.number,
  isDlsActive: PropTypes.bool,
  relativePage: PropTypes.number,
  activePages: PropTypes.array,
  isDoublePage: PropTypes.bool,
  pageWidth: PropTypes.number,
  pageHeight: PropTypes.number,
  // classes: PropTypes.object.isRequired,
  goToDestination: PropTypes.func.isRequired,
  resizableWidth: PropTypes.number,
  setResizableWidth: PropTypes.func.isRequired,
  showModal: PropTypes.func.isRequired,
  isPresentationMode: PropTypes.bool.isRequired,
  isAbleToPrint: PropTypes.bool.isRequired,
  bookTitle: PropTypes.string.isRequired,
  notesDlsOnly: PropTypes.bool.isRequired,
  pagesHaveNotes: PropTypes.bool.isRequired,
  showCutout: PropTypes.bool.isRequired,
  padLeftSide: PropTypes.bool.isRequired,
  padRightSide: PropTypes.bool.isRequired
};

export default connect(mapStateToProps)(Annotationeer);
